import type {
  CampaignRankingRawMetrics,
  CampaignPerformanceScorecard,
  CampaignExecutionScorecard,
  CampaignRanking,
} from '../types/campaign.types';

/**
 * Min-Max normalization
 */
const normalize = (value: number, min: number, max: number, invert = false): number => {
  if (max === min) return 50; // Neutral baseline if all values are the same
  let normalized = ((value - min) / (max - min)) * 100;
  if (invert) {
    normalized = 100 - normalized;
  }
  return Math.max(0, Math.min(100, normalized)); // Clamp 0-100
};

export const calculateCampaignRanking = (
  campaignId: string,
  campaignName: string,
  metrics: CampaignRankingRawMetrics,
  minMaxMetrics: {
    minViews: number;
    maxViews: number;
    minEngagement: number;
    maxEngagement: number;
    minER: number;
    maxER: number;
  }
): CampaignRanking => {
  const {
    totalViews,
    totalEngagement,
    spend,
    contractedDeliverables,
    completedDeliverables,
    deliverableProgress,
    timelineProgress,
    paymentCompletionPercentage,
  } = metrics;

  // Raw calculated stats
  const engagementRate = totalViews > 0 ? (totalEngagement / totalViews) * 100 : 0;

  // --- 1. Performance Score (75% Weight) ---
  const erNormalized = normalize(engagementRate, minMaxMetrics.minER, minMaxMetrics.maxER);
  const viewsNormalized = normalize(totalViews, minMaxMetrics.minViews, minMaxMetrics.maxViews);
  const engagementNormalized = normalize(totalEngagement, minMaxMetrics.minEngagement, minMaxMetrics.maxEngagement);

  const performanceTotalScore =
    erNormalized * 0.45 + viewsNormalized * 0.35 + engagementNormalized * 0.20;

  const performance: CampaignPerformanceScorecard = {
    engagementScore: erNormalized,
    viewsScore: viewsNormalized,
    engagementVolumeScore: engagementNormalized,
    totalScore: performanceTotalScore,
    engagementRate,
  };

  // --- 2. Execution Score (25% Weight) ---
  const deliverableCompletionScore =
    contractedDeliverables > 0
      ? (completedDeliverables / contractedDeliverables) * 100
      : 100;

  let timelineAdherenceScore = 100;
  if (timelineProgress > 0) {
    const deviation = timelineProgress - deliverableProgress;
    timelineAdherenceScore = Math.max(0, 100 - Math.max(0, deviation) * 2);
  }

  // Budget management (dummy for now, assuming actual = expected if not provided)
  const budgetManagementScore = 100;

  const paymentDisciplineScore = paymentCompletionPercentage;

  const executionTotalScore =
    deliverableCompletionScore * 0.40 +
    timelineAdherenceScore * 0.20 +
    budgetManagementScore * 0.20 +
    paymentDisciplineScore * 0.20;

  const execution: CampaignExecutionScorecard = {
    deliverableCompletionScore,
    timelineAdherenceScore,
    budgetManagementScore,
    paymentDisciplineScore,
    totalScore: executionTotalScore,
  };

  // --- Overall ---
  const overallScore = Math.round(performanceTotalScore * 0.75 + executionTotalScore * 0.25);

  let tier: 'High' | 'Medium' | 'Low' = 'Low';
  if (overallScore >= 70) {
    tier = 'High';
  } else if (overallScore >= 35) {
    tier = 'Medium';
  }

  return {
    id: campaignId,
    name: campaignName,
    overallScore,
    tier,
    performance,
    execution,
    metrics,
  };
};
